import { useEffect, useState } from 'react';
import { dealsAPI, contactsAPI, companiesAPI, productsAPI, usersAPI } from '../api';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, DollarSign, Calendar as CalendarIcon, GripVertical, Edit, Trash2, Package, ShoppingCart, Eye, User, Building, Mail, MessageSquare, X, ListTodo, CheckCircle2, Phone, Bot } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ThreadedComments } from '../components/ThreadedComments';
import { EmailCompose } from '../components/EmailCompose';
import { AssigneeSelect } from '../components/AssigneeSelect';

const stages = [
  { id: 'prospecting', label: 'Prospecting', color: 'bg-slate-500' },
  { id: 'qualification', label: 'Qualification', color: 'bg-blue-500' },
  { id: 'proposal', label: 'Proposal', color: 'bg-amber-500' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-purple-500' },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500' },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500' },
];

const productTypeColors = {
  pump: 'bg-blue-100 text-blue-700',
  part: 'bg-amber-100 text-amber-700',
  accessory: 'bg-purple-100 text-purple-700',
  service: 'bg-emerald-100 text-emerald-700',
  other: 'bg-slate-100 text-slate-600'
};

export const DealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEmailComposeOpen, setIsEmailComposeOpen] = useState(false);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [dealTasks, setDealTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    status: 'pending'
  });
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    stage: 'prospecting',
    contact_id: '',
    company_id: '',
    expected_close_date: null,
    probability: 10,
    notes: '',
    source: '',
    line_items: []
  });
  const [newLineItem, setNewLineItem] = useState({ product_id: '', quantity: 1, discount_percent: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, contactsRes, companiesRes, productsRes, usersRes] = await Promise.all([
        dealsAPI.getAll(),
        contactsAPI.getAll(),
        companiesAPI.getAll(),
        productsAPI.getAll(),
        usersAPI.getAll()
      ]);
      setDeals(dealsRes.data);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
      setProducts(productsRes.data.filter(p => p.is_active));
      setUsers(usersRes.data.users || usersRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleClickToCall = async (phoneNumber, contactName) => {
    if (!phoneNumber) {
      toast.error('No phone number available for this contact');
      return;
    }
    try {
      const { voipAPI } = await import('../api');
      await voipAPI.initiateCall({
        to_number: phoneNumber,
        from_extension: 'default',
        record_call: true,
        notes: `Outbound call to deal contact: ${contactName}`
      });
      toast.success(`Initiating call to ${phoneNumber}`);
    } catch (error) {
      // Fallback to tel: link if VoIP fails
      window.location.href = `tel:${phoneNumber}`;
      toast.info(`Opening phone dialer for ${phoneNumber}`);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLineItem = () => {
    if (!newLineItem.product_id) {
      toast.error('Please select a product');
      return;
    }
    const product = products.find(p => p.id === newLineItem.product_id);
    if (!product) return;

    const quantity = parseInt(newLineItem.quantity) || 1;
    const discount = parseFloat(newLineItem.discount_percent) || 0;
    const unitPrice = product.price;
    const total = unitPrice * quantity * (1 - discount / 100);

    const item = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      product_type: product.product_type || 'other',
      quantity,
      unit_price: unitPrice,
      discount_percent: discount,
      total
    };

    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, item]
    }));
    setNewLineItem({ product_id: '', quantity: 1, discount_percent: 0 });
  };

  const handleRemoveLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotalValue = () => {
    return formData.line_items.reduce((sum, item) => sum + item.total, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        value: formData.line_items.length > 0 ? calculateTotalValue() : parseFloat(formData.value) || 0,
        expected_close_date: formData.expected_close_date ? formData.expected_close_date.toISOString() : null,
        contact_id: formData.contact_id || null,
        company_id: formData.company_id || null
      };

      if (editingDeal) {
        await dealsAPI.update(editingDeal.id, submitData);
        toast.success('Deal updated');
      } else {
        await dealsAPI.create(submitData);
        toast.success('Deal created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deal');
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title,
      value: deal.value,
      stage: deal.stage,
      contact_id: deal.contact_id || '',
      company_id: deal.company_id || '',
      expected_close_date: deal.expected_close_date ? new Date(deal.expected_close_date) : null,
      probability: deal.probability || 10,
      notes: deal.notes || '',
      source: deal.source || '',
      line_items: deal.line_items || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await dealsAPI.delete(id);
      toast.success('Deal deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const handleStageChange = async (dealId, newStage) => {
    try {
      await dealsAPI.update(dealId, { stage: newStage });
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const resetForm = () => {
    setEditingDeal(null);
    setFormData({
      title: '',
      value: '',
      stage: 'prospecting',
      contact_id: '',
      company_id: '',
      expected_close_date: null,
      probability: 10,
      notes: '',
      source: '',
      line_items: []
    });
    setNewLineItem({ product_id: '', quantity: 1, discount_percent: 0 });
  };

  // Task functions
  const fetchDealTasks = async (dealId) => {
    try {
      const response = await dealsAPI.getTasks(dealId);
      setDealTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch deal tasks:', error);
      setDealTasks([]);
    }
  };

  const openTaskDialog = (deal) => {
    setSelectedDeal(deal);
    setTaskForm({
      title: `Task for ${deal.title}`,
      description: '',
      assigned_to: '',
      due_date: '',
      priority: 'medium',
      status: 'pending'
    });
    fetchDealTasks(deal.id);
    setIsTaskDialogOpen(true);
  };

  const handleCreateTask = async () => {
    if (!selectedDeal || !taskForm.title) return;
    
    try {
      await dealsAPI.createTask(selectedDeal.id, taskForm);
      toast.success('Task created successfully');
      setIsTaskDialogOpen(false);
      fetchDealTasks(selectedDeal.id);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? `${contact.first_name} ${contact.last_name}` : '-';
  };

  const getContact = (contactId) => {
    return contacts.find(c => c.id === contactId);
  };

  const openDealDetail = (deal) => {
    setSelectedDeal(deal);
    setIsDetailOpen(true);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '-';
  };

  // Group deals by stage for Kanban view
  const dealsByStage = stages.reduce((acc, stage) => {
    acc[stage.id] = deals.filter(deal => deal.stage === stage.id);
    return acc;
  }, {});

  // Group products by type for easier selection
  const productsByType = {
    pump: products.filter(p => p.product_type === 'pump'),
    part: products.filter(p => p.product_type === 'part'),
    accessory: products.filter(p => p.product_type === 'accessory'),
    service: products.filter(p => p.product_type === 'service'),
    other: products.filter(p => !p.product_type || p.product_type === 'other')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Deals</h1>
          <p className="text-muted-foreground mt-1">Manage your sales pipeline</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-deal-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Deal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingDeal ? 'Edit Deal' : 'Create New Deal'}</DialogTitle>
              <DialogDescription>
                {editingDeal ? 'Update deal details' : 'Add products to build your deal'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="title">Deal Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="e.g., Pump System Installation"
                    required
                    data-testid="deal-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact</Label>
                  <Select value={formData.contact_id || "none"} onValueChange={(v) => handleChange('contact_id', v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="deal-contact-select">
                      <SelectValue placeholder="Select contact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {contacts.map(contact => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select value={formData.company_id || "none"} onValueChange={(v) => handleChange('company_id', v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="deal-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Products Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-slate-600" />
                  <Label className="text-base font-medium">Products & Services</Label>
                </div>
                
                {/* Add Product */}
                <div className="flex gap-2 items-end p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1 space-y-2">
                    <Label>Select Product</Label>
                    <Select value={newLineItem.product_id} onValueChange={(v) => setNewLineItem(p => ({ ...p, product_id: v }))}>
                      <SelectTrigger data-testid="deal-product-select">
                        <SelectValue placeholder="Choose a product..." />
                      </SelectTrigger>
                      <SelectContent>
                        {productsByType.pump.length > 0 && (
                          <>
                            <SelectItem value="__header_pump" disabled className="font-bold text-blue-700">
                              🔵 Pumps
                            </SelectItem>
                            {productsByType.pump.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.price)} ({p.sku})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {productsByType.part.length > 0 && (
                          <>
                            <SelectItem value="__header_part" disabled className="font-bold text-amber-700">
                              🟡 Parts
                            </SelectItem>
                            {productsByType.part.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.price)} ({p.sku})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {productsByType.accessory.length > 0 && (
                          <>
                            <SelectItem value="__header_accessory" disabled className="font-bold text-purple-700">
                              🟣 Accessories
                            </SelectItem>
                            {productsByType.accessory.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.price)} ({p.sku})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {productsByType.service.length > 0 && (
                          <>
                            <SelectItem value="__header_service" disabled className="font-bold text-emerald-700">
                              🟢 Services
                            </SelectItem>
                            {productsByType.service.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.price)} ({p.sku})
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {productsByType.other.length > 0 && (
                          <>
                            <SelectItem value="__header_other" disabled className="font-bold text-slate-600">
                              ⚪ Other
                            </SelectItem>
                            {productsByType.other.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.price)} ({p.sku})
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24 space-y-2">
                    <Label>Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newLineItem.quantity}
                      onChange={(e) => setNewLineItem(p => ({ ...p, quantity: e.target.value }))}
                      data-testid="deal-quantity-input"
                    />
                  </div>
                  <div className="w-28 space-y-2">
                    <Label>Discount %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newLineItem.discount_percent}
                      onChange={(e) => setNewLineItem(p => ({ ...p, discount_percent: e.target.value }))}
                      data-testid="deal-discount-input"
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={handleAddLineItem} data-testid="add-product-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Line Items Table */}
                {formData.line_items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Discount</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.line_items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.product_name}</p>
                                <p className="text-xs text-muted-foreground">{item.product_sku}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={productTypeColors[item.product_type] || productTypeColors.other}>
                                {item.product_type || 'other'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{item.discount_percent}%</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.total)}</TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveLineItem(index)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={5} className="text-right font-bold">Deal Total:</TableCell>
                          <TableCell className="text-right font-bold text-lg">{formatCurrency(calculateTotalValue())}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Manual Value Entry (if no products) */}
                {formData.line_items.length === 0 && (
                  <div className="space-y-2">
                    <Label>Or enter deal value manually</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        value={formData.value}
                        onChange={(e) => handleChange('value', e.target.value)}
                        className="pl-9"
                        placeholder="0.00"
                        data-testid="deal-value-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Deal Settings */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select value={formData.stage} onValueChange={(v) => handleChange('stage', v)}>
                    <SelectTrigger data-testid="deal-stage-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map(stage => (
                        <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Probability (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.probability}
                    onChange={(e) => handleChange('probability', parseInt(e.target.value) || 0)}
                    data-testid="deal-probability-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected Close</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="deal-close-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.expected_close_date ? format(formData.expected_close_date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.expected_close_date}
                        onSelect={(date) => handleChange('expected_close_date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Additional notes about this deal..."
                  rows={3}
                  data-testid="deal-notes-input"
                />
              </div>

              <DialogFooter>
                <Button type="submit" data-testid="deal-submit-btn">
                  {editingDeal ? 'Update Deal' : 'Create Deal'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Deals</p>
            <p className="text-2xl font-bold">{deals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Pipeline Value</p>
            <p className="text-2xl font-bold">{formatCurrency(deals.reduce((sum, d) => sum + (d.value || 0), 0))}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Won Deals</p>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(deals.filter(d => d.stage === 'closed_won').reduce((sum, d) => sum + (d.value || 0), 0))}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Avg Deal Size</p>
            <p className="text-2xl font-bold">
              {formatCurrency(deals.length > 0 ? deals.reduce((sum, d) => sum + (d.value || 0), 0) / deals.length : 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board */}
      <ScrollArea className="w-full">
        <div className="flex gap-4 pb-4 min-w-max">
          {stages.map((stage) => (
            <div key={stage.id} className="w-80 flex-shrink-0">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                      <CardTitle className="text-sm font-medium">{stage.label}</CardTitle>
                    </div>
                    <Badge variant="outline">{dealsByStage[stage.id].length}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(dealsByStage[stage.id].reduce((sum, d) => sum + (d.value || 0), 0))}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {dealsByStage[stage.id].map((deal) => (
                    <Card key={deal.id} className="cursor-pointer hover:shadow-md transition-shadow" data-testid={`deal-card-${deal.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-sm">{deal.title}</h4>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openDealDetail(deal)} data-testid={`deal-view-${deal.id}`}>
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openTaskDialog(deal)} title="Assign Task">
                              <ListTodo className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(deal)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(deal.id)}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(deal.value)}</p>
                        {deal.line_items && deal.line_items.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <ShoppingCart className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{deal.line_items.length} products</span>
                          </div>
                        )}
                        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                          {deal.contact_id && <p>Contact: {getContactName(deal.contact_id)}</p>}
                          {deal.company_id && <p>Company: {getCompanyName(deal.company_id)}</p>}
                          {deal.expected_close_date && (
                            <p>Close: {format(new Date(deal.expected_close_date), 'MMM d, yyyy')}</p>
                          )}
                        </div>
                        <div className="mt-3">
                          <Select value={deal.stage} onValueChange={(v) => handleStageChange(deal.id, v)}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {stages.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {dealsByStage[stage.id].length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No deals</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Deal Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDeal && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedDeal.title}</DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-1">
                      <Badge className={stages.find(s => s.id === selectedDeal.stage)?.color + ' text-white'}>
                        {stages.find(s => s.id === selectedDeal.stage)?.label}
                      </Badge>
                      <span className="text-lg font-bold text-emerald-600">{formatCurrency(selectedDeal.value)}</span>
                    </DialogDescription>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setIsDetailOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-6 mt-4">
                {/* Main Content */}
                <div className="col-span-2 space-y-6">
                  {/* Deal Info */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Deal Information</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Expected Close</p>
                        <p className="font-medium">
                          {selectedDeal.expected_close_date 
                            ? format(new Date(selectedDeal.expected_close_date), 'MMM d, yyyy')
                            : '-'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Probability</p>
                        <p className="font-medium">{selectedDeal.probability || 0}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Source</p>
                        <p className="font-medium">{selectedDeal.source || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Created</p>
                        <p className="font-medium">
                          {selectedDeal.created_at 
                            ? formatDistanceToNow(new Date(selectedDeal.created_at), { addSuffix: true })
                            : '-'
                          }
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Line Items */}
                  {selectedDeal.line_items && selectedDeal.line_items.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Products ({selectedDeal.line_items.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product</TableHead>
                              <TableHead>Qty</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead>Discount</TableHead>
                              <TableHead className="text-right">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedDeal.line_items.map((item, idx) => {
                              const product = products.find(p => p.id === item.product_id);
                              const lineTotal = (product?.price || 0) * item.quantity * (1 - (item.discount_percent || 0) / 100);
                              return (
                                <TableRow key={idx}>
                                  <TableCell>
                                    <div>
                                      <p className="font-medium">{product?.name || 'Unknown'}</p>
                                      <p className="text-xs text-muted-foreground">{product?.sku}</p>
                                    </div>
                                  </TableCell>
                                  <TableCell>{item.quantity}</TableCell>
                                  <TableCell>{formatCurrency(product?.price || 0)}</TableCell>
                                  <TableCell>{item.discount_percent || 0}%</TableCell>
                                  <TableCell className="text-right font-medium">{formatCurrency(lineTotal)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}

                  {/* Notes */}
                  {selectedDeal.notes && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Notes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{selectedDeal.notes}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Comments */}
                  <Card>
                    <CardContent className="pt-6">
                      <ThreadedComments 
                        itemType="deals" 
                        itemId={selectedDeal.id}
                        showInternal={true}
                        maxHeight={300}
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  {/* Contact Info */}
                  {selectedDeal.contact_id && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Contact
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Link 
                          to={`/contacts/${selectedDeal.contact_id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {getContactName(selectedDeal.contact_id)}
                        </Link>
                        {getContact(selectedDeal.contact_id)?.email && (
                          <p className="text-sm text-muted-foreground">
                            {getContact(selectedDeal.contact_id)?.email}
                          </p>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setIsEmailComposeOpen(true)}
                          data-testid="deal-email-contact-btn"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Email Contact
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Company Info */}
                  {selectedDeal.company_id && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Company
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Link 
                          to={`/companies`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {getCompanyName(selectedDeal.company_id)}
                        </Link>
                      </CardContent>
                    </Card>
                  )}

                  {/* Actions */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => {
                          setIsDetailOpen(false);
                          handleEdit(selectedDeal);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Deal
                      </Button>
                      <Select 
                        value={selectedDeal.stage} 
                        onValueChange={(v) => {
                          handleStageChange(selectedDeal.id, v);
                          setSelectedDeal({ ...selectedDeal, stage: v });
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Change Stage" />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <EmailCompose
        open={isEmailComposeOpen}
        onOpenChange={setIsEmailComposeOpen}
        contact={selectedDeal?.contact_id ? getContact(selectedDeal.contact_id) : null}
        dealId={selectedDeal?.id}
        onEmailSent={() => {
          toast.success('Email sent to contact');
        }}
      />

      {/* Task Assignment Dialog */}
      <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
            <DialogDescription>
              Create a task for deal: {selectedDeal?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Task Title</Label>
              <Input 
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                placeholder="e.g., Follow up with client"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Add details about this task..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <AssigneeSelect
                  value={taskForm.assigned_to}
                  onValueChange={(v) => setTaskForm({...taskForm, assigned_to: v === "unassigned" ? "" : v})}
                  includeAI={true}
                  placeholder="Select assignee"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm({...taskForm, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Input 
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
              />
            </div>

            {dealTasks.length > 0 && (
              <div>
                <Label className="mb-2 block">Existing Tasks ({dealTasks.length})</Label>
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {dealTasks.map((task) => (
                      <div key={task.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          {task.status === 'completed' ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <ListTodo className="h-4 w-4 text-slate-400" />
                          )}
                          <span>{task.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {task.assigned_to_name && (
                            <span className="text-xs text-muted-foreground">{task.assigned_to_name}</span>
                          )}
                          <Badge variant={task.status === 'pending' ? 'secondary' : task.status === 'completed' ? 'default' : 'outline'}>
                            {task.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={!taskForm.title}>
              <ListTodo className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealsPage;
